import { useSearchParams } from 'react-router-dom';
import { useLanguage, useSeo } from '../i18n/LanguageContext';
import { IMAGES } from '../data/images';
import { Instagram } from 'lucide-react';
import { Eyebrow, MaskedLines, Reveal, TikTokIcon } from '../components/bits';
import PageHero from '../components/PageHero';
import QuoteForm from '../components/QuoteForm';

export default function Contact() {
  const { t } = useLanguage();
  useSeo('contact');
  const c = t.contactPage;
  const [params] = useSearchParams();
  const initialService = params.get('service') || '';

  return (
    <main>
      <PageHero eyebrow={c.infoEyebrow} lines={[c.line1, c.line2]} sub={c.sub} img={IMAGES.contactSide} alt={c.alt} />

      <section className="noise-bg bg-cream py-20 md:py-32" data-testid="quote-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-6 sm:px-12 lg:grid-cols-12 lg:gap-16 lg:px-20">
          <div className="lg:col-span-5">
            <Eyebrow>{c.infoEyebrow}</Eyebrow>
            <MaskedLines
              as="h2"
              lines={[c.infoHeading]}
              inView
              delay={0.1}
              className="mt-6 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-ink sm:text-4xl"
            />
            <Reveal delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-neutral-600 sm:text-lg">{c.infoBody}</p>
            </Reveal>
            <Reveal delay={0.25}>
              <dl className="mt-10 space-y-6 border-t border-ink/10 pt-8">
                {[
                  {
                    label: c.phone,
                    id: 'contact-phone',
                    phones: [
                      { tag: c.phoneEnLabel, num: c.phoneEn },
                      { tag: c.phoneFrLabel, num: c.phoneFr },
                    ],
                  },
                  { label: c.email, value: c.emailValue, id: 'contact-email', href: `mailto:${c.emailValue}` },
                  { label: c.areaLabel, value: c.areaValue, id: 'contact-area' },
                ].map((row) => (
                  <div key={row.id} className="flex items-baseline justify-between gap-6" data-testid={row.id}>
                    <dt className="font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-forest">
                      {row.label}
                    </dt>
                    <dd className="text-right text-sm text-neutral-600">
                      {row.phones ? (
                        <span className="flex flex-col gap-1.5">
                          {row.phones.map((p) => (
                            <span key={p.num}>
                              <span className="mr-2 font-mono text-[10px] uppercase tracking-[0.2em] text-neutral-400">
                                {p.tag}
                              </span>
                              <a
                                href={`tel:+1${p.num.replace(/[^0-9]/g, '')}`}
                                data-testid={`${row.id}-${p.tag === c.phoneEnLabel ? 'en' : 'fr'}`}
                                className="font-semibold text-ink underline decoration-[#C6A15B] underline-offset-4 transition-colors hover:text-forest"
                              >
                                {p.num}
                              </a>
                            </span>
                          ))}
                        </span>
                      ) : row.href ? (
                        <a href={row.href} className="underline decoration-[#C6A15B] underline-offset-4 transition-colors hover:text-forest">
                          {row.value}
                        </a>
                      ) : (
                        row.value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </Reveal>
            <Reveal delay={0.28}>
              <p className="mt-10 font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-forest">
                {c.followUs}
              </p>
              <div className="mt-4 flex items-center gap-3">
                <a
                  href="https://www.instagram.com/entretien.deluxe"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="contact-social-instagram"
                  className="flex h-11 w-11 items-center justify-center border border-ink/15 text-ink transition-colors hover:border-forest hover:text-forest"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href="https://www.tiktok.com/@deluxe.entretien"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="TikTok"
                  data-testid="contact-social-tiktok"
                  className="flex h-11 w-11 items-center justify-center border border-ink/15 text-ink transition-colors hover:border-forest hover:text-forest"
                >
                  <TikTokIcon className="h-4 w-4" />
                </a>
              </div>
            </Reveal>
            <Reveal delay={0.34}>
              <p className="mt-10 max-w-md border-l-2 border-[#C6A15B] pl-5 text-sm leading-relaxed text-neutral-600">
                {c.note}
              </p>
            </Reveal>
            <Reveal delay={0.35} className="mt-12 hidden lg:block">
              <div className="overflow-hidden">
                <img src={IMAGES.contactSide} alt={c.alt} loading="lazy" className="aspect-[4/3] w-full object-cover" />
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="border border-ink/10 bg-white p-7 sm:p-10" data-testid="quote-form-panel">
              <QuoteForm key={initialService} initialService={initialService} />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ink py-20 md:py-28" data-testid="contact-area-section">
        <div className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
          <Eyebrow dark>{t.area.eyebrow}</Eyebrow>
          <MaskedLines
            as="h2"
            lines={[t.area.line1, t.area.line2]}
            inView
            delay={0.1}
            className="mt-6 font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl"
          />
          <Reveal delay={0.2}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-white/70 sm:text-lg">{t.area.body}</p>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
